import React, { useEffect, useState } from "react";
import { Row, Col, Input, Form, Container } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "components/VerticalLayout/Footer";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { get, post, put } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import {
  getAllCountries,
  getCitiesForState,
  getStatesForCountry,
} from "helpers/utils/locationUtil";
import { zipcode } from "helpers/validations/regex";

function AddShippingBilling() {
  const { customerId } = useParams();
  const [isAddressSame, setIsAddressSame] = useState({
    sameAsCustomer: false,
    sameAsBilling: false,
  });

  // Separate states for billing and shipping locations
  const [billingLocations, setBillingLocations] = useState({
    states: [],
    cities: [],
  });

  const [shippingLocations, setShippingLocations] = useState({
    states: [],
    cities: [],
  });

  const [countries, setCountries] = useState([]);
  const [customerData, setCustomerData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const initializeData = async () => {
      const fetchedCountries = getAllCountries();
      setCountries(fetchedCountries);

      try {
        const customerFetchedData = await get(
          api.GET_CUSTOMER_BY_ID(customerId)
        );
        console.log("customerFetchedData",customerFetchedData);
        
        const customer = customerFetchedData?.customerPageResponse?.data[0];
        setCustomerData(customer);

        // Initialize billing locations if exists
        if (customer?.billingAddress?.country) {
          const billingStates = getStatesForCountry(
            customer.billingAddress.country
          );
          setBillingLocations((prev) => ({ ...prev, states: billingStates }));

          if (customer.billingAddress.province) {
            const billingCities = getCitiesForState(
              customer.billingAddress.country,
              customer.billingAddress.province
            );
            setBillingLocations((prev) => ({ ...prev, cities: billingCities }));
          }
        }

        // Initialize shipping locations if exists
        if (customer?.shippingAddress?.country) {
          const shippingStates = getStatesForCountry(
            customer.shippingAddress.country
          );
          setShippingLocations((prev) => ({ ...prev, states: shippingStates }));

          if (customer.shippingAddress.province) {
            const shippingCities = getCitiesForState(
              customer.shippingAddress.country,
              customer.shippingAddress.province
            );
            setShippingLocations((prev) => ({
              ...prev,
              cities: shippingCities,
            }));
          }
        }
      } catch (error) {
        console.log("Error fetching data", error);
      }
    };

    initializeData();
  }, [customerId]);

  const handleBillingCountryChange = (e) => {
    const selectedCountry = e.target.value;
    validation.setFieldValue("billingCountry", selectedCountry);

    // Update billing states and reset cities
    const fetchedStates = getStatesForCountry(selectedCountry);
    setBillingLocations({ states: fetchedStates, cities: [] });

    // Reset dependent fields
    validation.setFieldValue("billingProvince", "");
    validation.setFieldValue("billingCity", "");

    // Update shipping address if same as billing is checked
    if (isAddressSame.sameAsBilling) {
      validation.setFieldValue("shippingCountry", selectedCountry);
      setShippingLocations({ states: fetchedStates, cities: [] });
      validation.setFieldValue("shippingProvince", "");
      validation.setFieldValue("shippingCity", "");
    }
  };

  const handleBillingStateChange = (e) => {
    const selectedState = e.target.value;
    validation.setFieldValue("billingProvince", selectedState);

    // Update billing cities
    const fetchedCities = getCitiesForState(
      validation.values.billingCountry,
      selectedState
    );
    setBillingLocations((prev) => ({ ...prev, cities: fetchedCities }));

    // Update shipping address if same as billing is checked
    if (isAddressSame.sameAsBilling) {
      validation.setFieldValue("shippingProvince", selectedState);
      setShippingLocations((prev) => ({ ...prev, cities: fetchedCities }));
      validation.setFieldValue("shippingCity", "");
    }
  };

  const handleShippingCountryChange = (e) => {
    const selectedCountry = e.target.value;
    validation.setFieldValue("shippingCountry", selectedCountry);

    // Update shipping states and reset cities
    const fetchedStates = getStatesForCountry(selectedCountry);
    setShippingLocations({ states: fetchedStates, cities: [] });

    // Reset dependent fields
    validation.setFieldValue("shippingProvince", "");
    validation.setFieldValue("shippingCity", "");
  };

  const handleShippingStateChange = (e) => {
    const selectedState = e.target.value;
    validation.setFieldValue("shippingProvince", selectedState);

    // Update shipping cities
    const fetchedCities = getCitiesForState(
      validation.values.shippingCountry,
      selectedState
    );
    setShippingLocations((prev) => ({ ...prev, cities: fetchedCities }));
  };

  const handlesameAsCustomer = (e) => {
    setIsAddressSame((prev) => ({ ...prev, sameAsCustomer: e.target.checked }));

    if (e.target.checked && customerData) {
      // Update billing address fields
      validation.setFieldValue("billingCountry", customerData.country || "");
      validation.setFieldValue("billingZipCode", customerData.zipCode || "");
      validation.setFieldValue("billingProvince", customerData.province || "");
      validation.setFieldValue("billingCity", customerData.city || "");
      validation.setFieldValue("billingStreet", customerData.address || "");

      // Update billing locations
      if (customerData.country) {
        const states = getStatesForCountry(customerData.country);
        setBillingLocations((prev) => ({ ...prev, states }));

        if (customerData.province) {
          const cities = getCitiesForState(
            customerData.country,
            customerData.province
          );
          setBillingLocations((prev) => ({ ...prev, cities }));
        }
      }
    }
  };

  const handleSameAsBilling = (e) => {
    setIsAddressSame((prev) => ({ ...prev, sameAsBilling: e.target.checked }));

    if (e.target.checked) {
      // Copy billing values to shipping
      validation.setFieldValue(
        "shippingCountry",
        validation.values.billingCountry
      );
      validation.setFieldValue(
        "shippingZipCode",
        validation.values.billingZipCode
      );
      validation.setFieldValue(
        "shippingProvince",
        validation.values.billingProvince
      );
      validation.setFieldValue("shippingCity", validation.values.billingCity);
      validation.setFieldValue(
        "shippingStreet",
        validation.values.billingStreet
      );

      // Copy billing locations to shipping
      setShippingLocations({
        states: billingLocations.states,
        cities: billingLocations.cities,
      });
    }
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      billingCountry: customerData?.billingAddress?.country || "",
      billingZipCode: customerData?.billingAddress?.zipCode || "",
      billingProvince: customerData?.billingAddress?.province || "",
      billingCity: customerData?.billingAddress?.city || "",
      billingStreet: customerData?.billingAddress?.street || "",
      shippingCountry: customerData?.shippingAddress?.country || "",
      shippingZipCode: customerData?.shippingAddress?.zipCode || "",
      shippingProvince: customerData?.shippingAddress?.province || "",
      shippingCity: customerData?.shippingAddress?.city || "",
      shippingStreet: customerData?.shippingAddress?.street || "",
    },

    validationSchema: Yup.object({
      billingCountry: Yup.string().required("Billing country is required"),
      billingZipCode: Yup.string()
        .required("Billing zip code is required")
        .matches(
          zipcode,
          "Zip code must be 4-8 characters long and can include only numbers"
        ),
      billingProvince: Yup.string().required("Billing state is required"),
      billingCity: Yup.string().required("Billing city is required"),
      billingStreet: Yup.string(),
      shippingCountry: Yup.string().required("Shipping country is required"),
      shippingZipCode: Yup.string()
        .required("Shipping zip code is required")
        .matches(
          zipcode,
          "Zip code must be 4-8 characters long and can include only numbers"
        ),
      shippingProvince: Yup.string().required("Shipping state is required"),
      shippingCity: Yup.string().required("Shipping city is required"),
      shippingStreet: Yup.string(),
    }),

    onSubmit: async (values) => {
      try {
        const payload = {
          shippingAddress: {
            street: values.shippingStreet,
            city: values.shippingCity,
            province: values.shippingProvince,
            zipCode: values.shippingZipCode,
            country: values.shippingCountry,
          },
          billingAddress: {
            street: values.billingStreet,
            city: values.billingCity,
            province: values.billingProvince,
            zipCode: values.billingZipCode,
            country: values.billingCountry,
          },
        };
        const responseData =
          customerData.billingAddress != null
            ? await put(api.UPDATE_CUSTOMER_ADDRESS_BY_ID(customerId), payload)
            : await post(
                api.CREATE_CUSTOMER_ADDRESS_BY_ID(customerId),
                payload
              );

        if (responseData) {
          showSuccessToast("Customer address added successfully");
          navigate("/customers");
        }
      } catch (error) {
        console.log("Error Submitting Data", error);
        showErrorToast("Failed to add customer address");
      }
    },
  });

  return (
    <div className="page-content">
      <Container fluid={true} style={{ padding: "0px", gap: "10px" }}>
        <PageSubHeader
          heading={"Add Billing and Shipping"}
          routePath={"Home"}
          pageName={"Add Billing - Shipping"}
        />
        <Form>
          <Row className="col-12 justify-content-around my-3">
            <Col className="col-5 px-0 text-nowrap d-flex justify-content-start gap-1">
              <Input
                type="checkbox"
                id="sameAsCustomer"
                name="sameAsCustomer"
                onChange={handlesameAsCustomer}
                onBlur={validation.handleBlur}
                value={isAddressSame.sameAsCustomer}
                style={{ cursor: "pointer" }}
              />
              <label htmlFor="sameAsCustomer" className="m-1">
                Billing address is the same as the Customer Address
              </label>
            </Col>
            <Col className="col-5"></Col>
          </Row>
          <Row className="justify-content-around">
            <Col md={5}>
              <h5 className="text-primary">Billing Address</h5>
              <Row className="mb-3">
                <label htmlFor="billingCountry" className="col-form-label">
                  Country
                  <span className="text-danger">*</span>{" "}
                </label>
                <Input
                  id="billingCountry"
                  name="billingCountry"
                  type="select"
                  className="form-control"
                  onChange={handleBillingCountryChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.billingCountry}
                  invalid={
                    validation.touched.billingCountry &&
                    validation.errors.billingCountry
                      ? true
                      : false
                  }
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </Input>
                {validation.touched.billingCountry &&
                validation.errors.billingCountry ? (
                  <div className="invalid-feedback">
                    {validation.errors.billingCountry}
                  </div>
                ) : null}
              </Row>

              <Row className="mb-3">
                <label htmlFor="billingProvince" className="col-form-label">
                  Province
                  <span className="text-danger">*</span>{" "}
                </label>
                <Input
                  id="billingProvince"
                  name="billingProvince"
                  type="select"
                  className="form-control"
                  onChange={handleBillingStateChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.billingProvince}
                  invalid={
                    validation.touched.billingProvince &&
                    validation.errors.billingProvince
                      ? true
                      : false
                  }
                >
                  <option value="">Select Province</option>
                  {billingLocations.states.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Input>
                {validation.touched.billingProvince &&
                validation.errors.billingProvince ? (
                  <div className="invalid-feedback">
                    {validation.errors.billingProvince}
                  </div>
                ) : null}
              </Row>

              <Row className="mb-3">
                <label htmlFor="billingCity" className="col-form-label">
                  City
                  <span className="text-danger">*</span>{" "}
                </label>
                <Input
                  id="billingCity"
                  name="billingCity"
                  type="select"
                  className="form-control"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.billingCity}
                  invalid={
                    validation.touched.billingCity &&
                    validation.errors.billingCity
                      ? true
                      : false
                  }
                >
                  <option value="">Select City</option>
                  {billingLocations.cities.map((city) => (
                    <option key={city.value} value={city.value}>
                      {city.label}
                    </option>
                  ))}
                </Input>
                {validation.touched.billingCity &&
                validation.errors.billingCity ? (
                  <div className="invalid-feedback">
                    {validation.errors.billingCity}
                  </div>
                ) : null}
              </Row>

              <Row className="mb-3">
                <label htmlFor="billingZipCode" className="col-form-label">
                  Zip Code
                  <span className="text-danger">*</span>{" "}
                </label>
                <Input
                  id="billingZipCode"
                  name="billingZipCode"
                  type="text"
                  className="form-control"
                  placeholder="Enter zip code"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.billingZipCode}
                  invalid={
                    validation.touched.billingZipCode &&
                    validation.errors.billingZipCode
                      ? true
                      : false
                  }
                />
                {validation.touched.billingZipCode &&
                  validation.errors.billingZipCode && (
                    <div className="invalid-feedback">
                      {validation.errors.billingZipCode}
                    </div>
                  )}
              </Row>

              <Row className="mb-3">
                <label htmlFor="billingStreet" className="col-form-label">
                  Address
                </label>
                <Input
                  type="textarea"
                  id="billingStreet"
                  name="billingStreet"
                  maxLength="225"
                  rows="2"
                  className="form-control"
                  placeholder="Enter your address"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.billingStreet}
                  invalid={
                    validation.touched.billingStreet &&
                    validation.errors.billingStreet
                      ? true
                      : false
                  }
                />
                {validation.touched.billingStreet &&
                  validation.errors.billingStreet && (
                    <div className="invalid-feedback">
                      {validation.errors.billingStreet}
                    </div>
                  )}
              </Row>
              <Row className="mb-3 text-nowrap ">
                <Col md={12} className="d-flex justify-content-start gap-1">
                  <Input
                    type="checkbox"
                    id="sameAsBilling"
                    name="sameAsBilling"
                    onChange={handleSameAsBilling}
                    onBlur={validation.handleBlur}
                    value={isAddressSame.sameAsBilling}
                    style={{ cursor: "pointer" }}
                  />
                  <label htmlFor="sameAsBilling" className="m-1">
                    Shipping address is the same as the Billing address
                  </label>
                </Col>
              </Row>
            </Col>
            <Col md={5}>
              <h5 className="text-primary">Shipping Address</h5>
              <Row className="mb-3">
                <label htmlFor="shippingCountry" className="col-form-label">
                  Country
                  <span className="text-danger">*</span>{" "}
                </label>
                <Input
                  id="shippingCountry"
                  name="shippingCountry"
                  type="select"
                  className="form-control"
                  onChange={handleShippingCountryChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.shippingCountry}
                  invalid={
                    validation.touched.shippingCountry &&
                    validation.errors.shippingCountry
                      ? true
                      : false
                  }
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </Input>
                {validation.touched.shippingCountry &&
                validation.errors.shippingCountry ? (
                  <div className="invalid-feedback">
                    {validation.errors.shippingCountry}
                  </div>
                ) : null}
              </Row>

              <Row className="mb-3">
                <label htmlFor="shippingProvince" className="col-form-label">
                  Province
                  <span className="text-danger">*</span>{" "}
                </label>
                <Input
                  id="shippingProvince"
                  name="shippingProvince"
                  type="select"
                  className="form-control"
                  onChange={handleShippingStateChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.shippingProvince}
                  invalid={
                    validation.touched.shippingProvince &&
                    validation.errors.shippingProvince
                      ? true
                      : false
                  }
                >
                  <option value="">Select Province</option>
                  {shippingLocations.states.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Input>
                {validation.touched.shippingProvince &&
                validation.errors.shippingProvince ? (
                  <div className="invalid-feedback">
                    {validation.errors.shippingProvince}
                  </div>
                ) : null}
              </Row>

              <Row className="mb-3">
                <label htmlFor="shippingCity" className="col-form-label">
                  City
                  <span className="text-danger">*</span>{" "}
                </label>
                <Input
                  id="shippingCity"
                  name="shippingCity"
                  type="select"
                  className="form-control"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.shippingCity}
                  invalid={
                    validation.touched.shippingCity &&
                    validation.errors.shippingCity
                      ? true
                      : false
                  }
                >
                  <option value="">Select City</option>
                  {shippingLocations.cities.map((city) => (
                    <option key={city.value} value={city.value}>
                      {city.label}
                    </option>
                  ))}
                </Input>
                {validation.touched.shippingCity &&
                validation.errors.shippingCity ? (
                  <div className="invalid-feedback">
                    {validation.errors.shippingCity}
                  </div>
                ) : null}
              </Row>

              <Row className="mb-3">
                <label htmlFor="shippingZipCode" className="col-form-label">
                  Zip Code
                  <span className="text-danger">*</span>{" "}
                </label>
                <Input
                  id="shippingZipCode"
                  name="shippingZipCode"
                  type="text"
                  className="form-control"
                  placeholder="Enter zip code"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.shippingZipCode}
                  invalid={
                    validation.touched.shippingZipCode &&
                    validation.errors.shippingZipCode
                      ? true
                      : false
                  }
                />
                {validation.touched.shippingZipCode &&
                  validation.errors.shippingZipCode && (
                    <div className="invalid-feedback">
                      {validation.errors.shippingZipCode}
                    </div>
                  )}
              </Row>

              <Row className="mb-3">
                <label htmlFor="shippingStreet" className="col-form-label">
                  Address
                </label>
                <Input
                  type="textarea"
                  id="shippingStreet"
                  name="shippingStreet"
                  maxLength="225"
                  rows="2"
                  className="form-control"
                  placeholder="Enter your address"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.shippingStreet}
                  invalid={
                    validation.touched.shippingStreet &&
                    validation.errors.shippingStreet
                      ? true
                      : false
                  }
                />
                {validation.touched.shippingStreet &&
                  validation.errors.shippingStreet && (
                    <div className="invalid-feedback">
                      {validation.errors.shippingStreet}
                    </div>
                  )}
              </Row>
            </Col>
          </Row>
        </Form>
      </Container>
      <Footer save={validation.handleSubmit} />
    </div>
  );
}

export default AddShippingBilling;
